import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SentenceVerb } from '@/types/article';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface VerbManagementProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sentences: string[];
  sentenceVerbs: { [index: number]: SentenceVerb[] };
  onSentenceVerbsChange: (sentenceVerbs: {
    [index: number]: SentenceVerb[];
  }) => void;
  onProcessArticle: () => void;
  isProcessing: boolean;
}

const VerbManagement = ({
  open,
  onOpenChange,
  sentences,
  sentenceVerbs,
  onSentenceVerbsChange,
  onProcessArticle,
  isProcessing,
}: VerbManagementProps) => {
  const { t } = useTranslation();

  const addVerb = (sentenceIndex: number) => {
    const newVerb: SentenceVerb = {
      german: '',
      english: '',
      vietnamese: '',
    };

    const updated = {
      ...sentenceVerbs,
      [sentenceIndex]: [...(sentenceVerbs[sentenceIndex] || []), newVerb],
    };
    onSentenceVerbsChange(updated);
  };

  const updateVerb = (
    sentenceIndex: number,
    verbIndex: number,
    field: keyof SentenceVerb,
    value: string
  ) => {
    const updated = {
      ...sentenceVerbs,
      [sentenceIndex]:
        sentenceVerbs[sentenceIndex]?.map((verb, idx) =>
          idx === verbIndex ? { ...verb, [field]: value } : verb
        ) || [],
    };
    onSentenceVerbsChange(updated);
  };

  const deleteVerb = (sentenceIndex: number, verbIndex: number) => {
    const updated = {
      ...sentenceVerbs,
      [sentenceIndex]:
        sentenceVerbs[sentenceIndex]?.filter((_, idx) => idx !== verbIndex) ||
        [],
    };
    onSentenceVerbsChange(updated);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Difficult Words & Verbs</DialogTitle>
          <DialogDescription>
            Review and edit the identified difficult words and verbs for each
            sentence. You can add, edit, or remove words as needed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {sentences.map((sentence, sentenceIndex) => (
            <div
              key={sentenceIndex}
              className="border rounded-lg p-4 space-y-4"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-muted-foreground">
                  Sentence {sentenceIndex + 1}
                </span>
                {sentenceIndex === 0 && (
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                    Title
                  </span>
                )}
              </div>

              <div className="bg-gray-50 p-3 rounded">
                <p className="text-sm font-medium">{sentence}</p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium">Wörter-Buch</h4>
                  <Button size="sm" onClick={() => addVerb(sentenceIndex)}>
                    <Plus className="h-3 w-3 mr-1" />
                    Add Word
                  </Button>
                </div>

                {(sentenceVerbs[sentenceIndex] || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">
                    {t('verbManagement.noDifficultWords')}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {(sentenceVerbs[sentenceIndex] || []).map(
                      (verb, verbIndex) => (
                        <div
                          key={verbIndex}
                          className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end border rounded p-3"
                        >
                          <div>
                            <Label className="text-xs">German Word</Label>
                            <Input
                              value={verb.german}
                              onChange={e =>
                                updateVerb(
                                  sentenceIndex,
                                  verbIndex,
                                  'german',
                                  e.target.value
                                )
                              }
                              placeholder={t('placeholders.germanWord')}
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">
                              English Translation
                            </Label>
                            <Input
                              value={verb.english}
                              onChange={e =>
                                updateVerb(
                                  sentenceIndex,
                                  verbIndex,
                                  'english',
                                  e.target.value
                                )
                              }
                              placeholder={t('placeholders.englishTranslation')}
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">
                              Vietnamese Translation
                            </Label>
                            <Input
                              value={verb.vietnamese}
                              onChange={e =>
                                updateVerb(
                                  sentenceIndex,
                                  verbIndex,
                                  'vietnamese',
                                  e.target.value
                                )
                              }
                              placeholder={t(
                                'placeholders.vietnameseTranslation'
                              )}
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() =>
                                deleteVerb(sentenceIndex, verbIndex)
                              }
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onProcessArticle} disabled={isProcessing}>
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Translating...
              </>
            ) : (
              'Continue to Translation'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VerbManagement;
